import { watch, watchEffect } from 'vue';
import { watchImmediate } from '@vueuse/core';
import { stopAll, type StopHandle } from '@/util/stopHandle';
import { positionalPart } from '@/domain/geometry/matrix';
import { type FittedStemAxis } from '@/views/3d-planner/fitting/fittedStem';
import type { AxisNode } from '@/lib/threeScene/nodes/axis';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore';
import type { PlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel.types';
import type { AxisMeasurement } from '@/domain/anatomy/measurements';
import { vector3, zero3 } from '@/domain/geometry/vector3';
import type { NumberArray3 } from '@/domain/geometry/apiVector';
import { useDeveloperSettings } from '@/stores/developerSettings';
import { planeTransform } from '@/lib/threeScene/nodes/plane';
import { logValidation } from '@/views/3d-planner/operations/logValidation';
import { formatMatrixBasis, formatMatrixEuler, indent, joinIndented } from '@/domain/geometry/formatMath';
import { computeBearingUrl, computeHeadUrl } from '@/views/3d-planner/fitting/componentUrls';
import { updateCrossSection } from '@/views/3d-planner/scene/crossSection';

/** Updates to the scene that relate to the stem, bearing and head. Reads domain off the Model, scene off the VM. */
export function updateStem(model: PlannerStore, scene: PlannerSceneViewModel): StopHandle {
    return stopAll(
        watchImmediate(
            () => scene.plannerMode !== 'cup',
            (show) => {
                scene.nodes.stemGroup.visible = show;
                scene.nodes.femoralGroup.visible = show;
            },
        ),
        watchImmediate(
            () => model.template?.stemUrl ?? null,
            (url) => (scene.nodes.stem.geometrySource = url),
        ),
        watchImmediate(
            () => (model.template && model.catalog ? computeHeadUrl(model) : null),
            (url) => (scene.nodes.head.geometrySource = url),
        ),
        watchImmediate(
            () => (model.template && model.catalog ? computeBearingUrl(model) : null),
            (url) => (scene.nodes.bearing.geometrySource = url),
        ),
        watchImmediate(
            () => model.fittedStem,
            (fittedStem) => {
                if (fittedStem) {
                    scene.nodes.stem.transform.copy(fittedStem.stemLocal);
                    scene.nodes.head.transform.copy(fittedStem.headLocal);
                    if (fittedStem.bearingLocal) {
                        scene.nodes.bearing.transform.copy(fittedStem.bearingLocal);
                    }
                }
            },
        ),
        watchEffect(() => _updateFemoralObjectTransforms(model, scene)),
        watchEffect(() => _updateGroupTransforms(model, scene)),
        watchEffect(() => _updateStemAxes(model, scene)),
        watchEffect(() => _updateFemoralAxes(model, scene)),
        _logStemChanges(model, scene),
        watchEffect(() => _updateCrossSectionTransforms(model, scene)),
        updateCrossSection(scene, scene.nodes.stemCoronalCrossSection),
        updateCrossSection(scene, scene.nodes.stemNeckCrossSection),
    );
}

function _logStemChanges(model: PlannerStore, scene: PlannerSceneViewModel): StopHandle {
    return stopAll(
        watch(
            () => (model.isLoading || !model.template ? null : model.template.stemTransform),
            (localTransform) => {
                if (localTransform != null) {
                    logValidation(
                        joinIndented(2)([
                            'Stem manual-transform:',
                            ...formatMatrixEuler(localTransform),
                        ]),
                    );
                }
            },
        ),
        watch(
            () =>
                model.isLoading
                    ? null
                    : {
                        stemGroup: scene.nodes.stemGroup.transform,
                        stem: scene.nodes.stem.transform,
                        head: scene.nodes.head.transform,
                    },
            (transforms) => {
                if (transforms) {
                    const { stemGroup, stem, head } = transforms;
                    const headWorld = stemGroup.clone().multiply(head);
                    const stemWorld = stemGroup.clone().multiply(stem);
                    logValidation(joinIndented(2)([
                        `Stem-group transforms (${scene.plannerMode !== 'stem' ? 'retracted' : 'native'}}):`,
                        'stem-group:',
                        ...indent(2)(formatMatrixBasis(stemGroup)),
                        'head (world):',
                        ...indent(2)(formatMatrixBasis(headWorld)),
                        'stem (world):',
                        ...indent(2)(formatMatrixBasis(stemWorld)),
                    ]));
                }
            },
            { deep: true },
        ),
    );
}

/**
 * Update the transform of the operative-femur and inner-cortical-surface relative to the femoral-group
 */
function _updateFemoralObjectTransforms(model: PlannerStore, scene: PlannerSceneViewModel): void {
    if (model.fittedStem) {
        // The femoral-group will be at the fitted-head-transform when the
        // femur is in its native (pre-operative) position, so the relative placement
        // of the femoral objects is the inverse of this transform.
        const femurInFemoralGroup = model.fittedStem.transform.clone().invert();
        scene.nodes.operativeFemur.transform.copy(femurInFemoralGroup);
        scene.nodes.operativeFemurInternal.transform.copy(femurInFemoralGroup);
        logValidation(joinIndented(2)([
            `Local femoral transforms:`,
            'femur:',
            ...indent(2)(formatMatrixBasis(scene.nodes.operativeFemur.transform)),
            'femur-internal:',
            ...indent(2)(formatMatrixBasis(scene.nodes.operativeFemurInternal.transform)),
        ]));
    } else {
        // If the fitted-head-transform is not available the femoral-group will
        // have an identity transform, so the femoral objects have the same.
        scene.nodes.operativeFemur.transform.identity();
        scene.nodes.operativeFemurInternal.transform.identity();
        logValidation('Local femoral transforms set to identity');
    }
}

function _updateGroupTransforms(model: PlannerStore, scene: PlannerSceneViewModel): void {
    if (model.template && model.fittedStem) {
        const stemGroupTransform = model.fittedStem.transform
            .clone()
            .multiply(model.template.stemTransform);
        scene.nodes.stemGroup.transform.copy(stemGroupTransform);
        scene.nodes.femoralGroup.transform.copy(model.fittedStem.transform);

        if (scene.plannerMode !== 'stem') {
            // If we are not in stem-mode we want to translate the stem-group so that its
            // position matches the cup-group (the head is 'inside' the cup)
            const hjc = positionalPart(scene.nodes.cupGroup.transform);
            scene.nodes.stemGroup.transform.setPosition(hjc);

            // Translate the femoral-group by the same amount, preserving the transform between
            // the femur and the stem (the stem-transform).
            const shiftedFemoralGroupPosition = positionalPart(model.fittedStem.transform)
                .add(hjc)
                .sub(positionalPart(stemGroupTransform));
            scene.nodes.femoralGroup.transform.setPosition(shiftedFemoralGroupPosition);
        }

        scene.nodes.stemGroup.showAxes = useDeveloperSettings().show3dFeatures || false;
    } else {
        scene.nodes.stemGroup.showAxes = false;
    }
}

function _updateStemAxes(model: PlannerStore, scene: PlannerSceneViewModel) {
    function updateStemAxis(state: FittedStemAxis | undefined, node: AxisNode) {
        if (state) {
            node.visible = useDeveloperSettings().show3dFeatures || false;
            node.transform.setPosition(state.position);
            node.direction.copy(state.direction);
        } else {
            node.visible = false;
        }
    }

    updateStemAxis(model.fittedStem?.axesLocal?.neckAxis, scene.nodes.stemNeckAxis);
    updateStemAxis(model.fittedStem?.axesLocal?.shaftAxis, scene.nodes.stemShaftAxis);
    updateStemAxis(model.fittedStem?.axesLocal?.paAxis, scene.nodes.stemPaAxis);
}

function _updateCrossSectionTransforms(model: PlannerStore, scene: PlannerSceneViewModel) {
    if (model.fittedStem?.axesLocal) {
        scene.nodes.stemCoronalCrossSection.transform = planeTransform(
            zero3(),
            model.fittedStem.axesLocal.paAxis.direction.clone().negate(),
        );
        scene.nodes.stemNeckCrossSection.transform = planeTransform(
            model.fittedStem.resectionPlaneLocal.origin,
            model.fittedStem.resectionPlaneLocal.normal,
            model.fittedStem.resectionPlaneLocal.x,
        );
    }
}

function _updateFemoralAxes(
    { femoralFeatures, fittedStem }: PlannerStore,
    { nodes }: PlannerSceneViewModel,
) {
    if (!femoralFeatures || !fittedStem) {
        nodes.femoralShaftAxis.visible = false;
        nodes.femoralProximalShaftAxis.visible = false;
        nodes.femoralNeckAxis.visible = false;
        nodes.femoralAnteversionNeckAxis.visible = false;
        nodes.femoralAnteversionCondylarAxis.visible = false;
        return;
    }

    const femurInFemoralGroup = fittedStem.transform.clone().invert();
    const { shaftAxis, proximalShaftAxis, neckAxis, anteversionNeck, anteversionCondylar } =
        femoralFeatures;

    const updateAxis = (node: AxisNode, direction: NumberArray3, position: NumberArray3) => {
        node.direction = vector3(direction).transformDirection(femurInFemoralGroup);
        node.transform.setPosition(vector3(position).applyMatrix4(femurInFemoralGroup));
        node.visible = useDeveloperSettings().show3dFeatures || false;
    };

    const updateAxisFromMeasurement = (node: AxisNode, measurement: AxisMeasurement) =>
        updateAxis(node, measurement.value, measurement.position);

    updateAxisFromMeasurement(nodes.femoralShaftAxis, shaftAxis);
    updateAxisFromMeasurement(nodes.femoralProximalShaftAxis, proximalShaftAxis);
    updateAxisFromMeasurement(nodes.femoralNeckAxis, neckAxis);

    updateAxis(nodes.femoralAnteversionCondylarAxis, anteversionCondylar, neckAxis.position);
    updateAxis(nodes.femoralAnteversionNeckAxis, anteversionNeck, neckAxis.position);
}
